/**
 * Hotjar Analytics Component
 * 
 * Automatically loads Hotjar tracking script when VITE_HOTJAR_ID is configured
 * Works across all pages without any additional setup
 * 
 * Usage: Add once in App.tsx, it will track all pages automatically
 * 
 * Environment Setup:
 * - Add VITE_HOTJAR_ID=your_site_id to .env.local
 * - Add VITE_HOTJAR_ID to Azure DevOps variable groups
 */

import { useEffect } from 'react'
import { config } from '@/lib/config'

export function Hotjar() {
  useEffect(() => {
    // Only load Hotjar if ID is configured
    if (!config.hotjarId) {
      console.log('Hotjar: Not configured (VITE_HOTJAR_ID not set)')
      return
    }

    // Check if Hotjar is already loaded
    if (window.hj) {
      console.log('Hotjar: Already loaded')
      return
    }

    // Load Hotjar tracking script
    console.log(`Hotjar: Loading with ID ${config.hotjarId}`)
    
    // Hotjar Tracking Code
    ;(function(h: Window & typeof globalThis, o: Document, t: string, j: string) {
      const hj: HotjarFn = h.hj ?? function(...args: unknown[]) {
        (hj.q = hj.q || []).push(args)
      }
      h.hj = hj
      const settings = { hjid: config.hotjarId, hjsv: 6 }
      h._hjSettings = settings
      const a = o.getElementsByTagName('head')[0]
      const r = o.createElement('script')
      r.async = true
      r.src = t + settings.hjid + j + settings.hjsv
      a.appendChild(r)
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=')

    console.log('Hotjar: Loaded successfully')
  }, [])

  // This component doesn't render anything
  return null
}

type HotjarFn = ((...args: unknown[]) => void) & { q?: unknown[][] }

// TypeScript declaration for Hotjar global. Both are absent until the script above has run.
declare global {
  interface Window {
    hj?: HotjarFn
    _hjSettings?: { hjid: number | string; hjsv: number }
  }
}
